import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { useBoardChartData } from '../../hooks/useBoardChartData';
import { BaseChartProps } from './types';
import { CHART_COLORS, CHART_STYLES, getCurrencySymbol, formatCompactNumber, formatCurrency } from './chartTheme';
import { useSetChartMetadata } from './ChartContext';

export const PauseResumeComparisonChart: React.FC<BaseChartProps> = () => {
    const { t } = useTranslation();
    const { finance, events, currencySettings, isLoading } = useBoardChartData();
    const currencySymbol = getCurrencySymbol(currencySettings?.default);

    const chartData = useMemo(() => {
        if (!events || !finance?.dailyBreakdown) return [];

        const statusEvents = events.filter((e: any) =>
            (e.category === 'campaign' || e.category === 'ad_set') &&
            ((e.type || e.eventType)?.includes('run_status') || (e.type || e.eventType)?.includes('status_changed'))
        );

        const byEntity = new Map<string, any[]>();
        statusEvents.forEach((e: any) => {
            const eid = e.entityId || 'unknown';
            if (!byEntity.has(eid)) byEntity.set(eid, []);
            byEntity.get(eid)!.push(e);
        });

        const sortedDates = Object.keys(finance.dailyBreakdown!).sort();
        const results: { name: string; prePause: number; postResume: number }[] = [];

        byEntity.forEach((evts, entityId) => {
            if (evts.length < 2) return;
            const sorted = evts.sort((a: any, b: any) => (a.date || '').localeCompare(b.date || ''));
            const pauseDate = sorted[0].date?.split('T')[0];
            const resumeDate = sorted[sorted.length - 1].date?.split('T')[0];
            if (!pauseDate || !resumeDate || pauseDate === resumeDate) return;

            const pauseIdx = sortedDates.indexOf(pauseDate);
            const resumeIdx = sortedDates.indexOf(resumeDate);

            const preDays = sortedDates.slice(Math.max(0, pauseIdx - 7), pauseIdx);
            const postDays = sortedDates.slice(resumeIdx, Math.min(sortedDates.length, resumeIdx + 7));

            const avgPre = preDays.length > 0 ? preDays.reduce((s, d) => s + (finance.dailyBreakdown![d]?.metrics.revenue || 0), 0) / preDays.length : 0;
            const avgPost = postDays.length > 0 ? postDays.reduce((s, d) => s + (finance.dailyBreakdown![d]?.metrics.revenue || 0), 0) / postDays.length : 0;

            const name = sorted[0].description?.substring(0, 20) || entityId.substring(0, 12);
            results.push({ name, prePause: avgPre, postResume: avgPost });
        });

        return results.slice(0, 8);
    }, [events, finance]);

    const isEmpty = !isLoading && chartData.length === 0;
    useSetChartMetadata({ isEmpty });

    return (
        <div className="w-full h-full bg-transparent overflow-hidden flex flex-col">
            <div className="flex-1 min-h-0 p-2">
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} margin={CHART_STYLES.margin.withAxis}>
                        <CartesianGrid strokeDasharray={CHART_STYLES.grid.strokeDasharray} vertical={false} stroke={CHART_STYLES.grid.stroke} opacity={CHART_STYLES.grid.opacity} />
                        <XAxis dataKey="name" {...CHART_STYLES.axis} tick={{ ...CHART_STYLES.axis.tick, fontSize: 9 }} />
                        <YAxis {...CHART_STYLES.axis} tickFormatter={(v) => formatCompactNumber(v, currencySymbol)} tick={{ ...CHART_STYLES.axis.tick, fontSize: 10 }} width={55} />
                        <Tooltip contentStyle={CHART_STYLES.tooltip.contentStyle} formatter={(v: number) => formatCurrency(v, currencySymbol)} />
                        <Legend wrapperStyle={{ fontSize: '10px' }} />
                        <Bar dataKey="prePause" name={t('charts.prePause', 'Pre-Pause') as string} fill={CHART_COLORS.gray[400]} radius={[3, 3, 0, 0]} maxBarSize={30} />
                        <Bar dataKey="postResume" name={t('charts.postResume', 'Post-Resume') as string} fill={CHART_COLORS.chart.line2} radius={[3, 3, 0, 0]} maxBarSize={30} />
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};
